import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  appendOrderBlankPromo,
  fetchOrderBlankText,
  type OrderBlankRequest,
} from '../api/orderBlank'
import {
  fetchSavedOrderBlankText,
  listSavedOrderBlanks,
  saveOrderBlank,
  type SavedOrderBlankDto,
} from '../api/orderBlankHistory'
import {
  addToCart,
  clearSupplierCart,
  groupCartBySupplier,
  setCartQuantity,
} from '../cart/cartStore'
import { useCart } from '../hooks/useCart'
import { formatRuDate } from '../utils/formatDate'
import { formatUnitLabel } from '../utils/formatUnitLabel'
import { shortenSupplierName } from '../utils/supplierName'

type CartScreenProps = {
  organizationId: number
}

type SubTab = 'cart' | 'history'

const formatMoney = (value: number): string =>
  new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 2,
  }).format(value)

const formatDate = (iso: string): string => {
  try {
    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(new Date(iso))
  } catch {
    return iso
  }
}

// standalone PWA: сначала нативный Web Share (навигатор шлёт в мессенджер/почту),
// иначе — фолбэк в буфер обмена (пользователь вставит в чат вручную).
const shareText = async (text: string): Promise<'shared' | 'copied' | 'failed'> => {
  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    try {
      await navigator.share({ text })
      return 'shared'
    } catch (error) {
      // Пользователь отменил шаринг — это не ошибка, не показываем алерт.
      if (error instanceof DOMException && error.name === 'AbortError') {
        return 'shared'
      }
      // Share недоступен по иной причине — падаем в clipboard.
    }
  }

  try {
    await navigator.clipboard.writeText(text)
    return 'copied'
  } catch {
    return 'failed'
  }
}

export const CartScreen = ({ organizationId }: CartScreenProps) => {
  const { lines } = useCart()
  const [subTab, setSubTab] = useState<SubTab>('cart')
  const [history, setHistory] = useState<SavedOrderBlankDto[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyError, setHistoryError] = useState<string | null>(null)

  const [sheetSupplierId, setSheetSupplierId] = useState<number | null>(null)
  const [deliveryDate, setDeliveryDate] = useState('')
  const [comment, setComment] = useState('')
  const [blankText, setBlankText] = useState('')
  const [blankLoading, setBlankLoading] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [viewText, setViewText] = useState<string | null>(null)

  const groups = useMemo(() => groupCartBySupplier(lines), [lines])
  const sheetGroup = groups.find((group) => group.supplierId === sheetSupplierId) ?? null

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true)
    setHistoryError(null)
    try {
      const items = await listSavedOrderBlanks(organizationId)
      setHistory(items)
    } catch (error) {
      setHistoryError(error instanceof Error ? error.message : 'Не удалось загрузить историю')
    } finally {
      setHistoryLoading(false)
    }
  }, [organizationId])

  useEffect(() => {
    if (subTab === 'history') {
      void loadHistory()
    }
  }, [loadHistory, subTab])

  const buildRequest = (supplierId: number): OrderBlankRequest | null => {
    const group = groups.find((entry) => entry.supplierId === supplierId)
    if (!group) {
      return null
    }
    return {
      organizationId,
      privateSupplierId: supplierId,
      desiredDeliveryDate: deliveryDate || undefined,
      comment: comment.trim() || undefined,
      lines: group.lines.map((line) => ({
        catalogItemId: line.catalogItemId,
        quantity: line.quantity,
        unit: formatUnitLabel(line.unit),
        unitPrice: line.unitPrice ?? undefined,
        note: line.note ?? undefined,
      })),
    }
  }

  const openSheet = async (supplierId: number) => {
    setSheetSupplierId(supplierId)
    setActionError(null)
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    setDeliveryDate(tomorrow.toISOString().slice(0, 10))
    setComment('')
    setBlankLoading(true)
    try {
      const request = buildRequest(supplierId)
      if (!request) {
        return
      }
      const text = appendOrderBlankPromo(await fetchOrderBlankText(request))
      setBlankText(text)
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Не удалось сформировать бланк')
      setBlankText('')
    } finally {
      setBlankLoading(false)
    }
  }

  useEffect(() => {
    if (sheetSupplierId == null || !deliveryDate) {
      return
    }
    const timer = window.setTimeout(() => {
      void (async () => {
        const request = buildRequest(sheetSupplierId)
        if (!request) {
          return
        }
        setBlankLoading(true)
        try {
          const text = appendOrderBlankPromo(await fetchOrderBlankText(request))
          setBlankText(text)
          setActionError(null)
        } catch (error) {
          setActionError(error instanceof Error ? error.message : 'Не удалось обновить бланк')
        } finally {
          setBlankLoading(false)
        }
      })()
    }, 400)
    return () => window.clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rebuild when date/comment change
  }, [comment, deliveryDate, sheetSupplierId])

  const handleCopy = async () => {
    if (!blankText) {
      return
    }
    try {
      await navigator.clipboard.writeText(blankText)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      setActionError('Не удалось скопировать. Выделите текст вручную.')
    }
  }

  const handleShare = async () => {
    if (sheetSupplierId == null) {
      return
    }
    const request = buildRequest(sheetSupplierId)
    if (!request || !blankText) {
      return
    }
    setActionError(null)
    try {
      const result = await shareText(blankText)
      if (result === 'failed') {
        setActionError('Не удалось поделиться. Скопируйте текст вручную.')
        return
      }
      await saveOrderBlank(request)
      clearSupplierCart(sheetSupplierId)
      setSheetSupplierId(null)
      setSubTab('history')
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Не удалось сохранить заявку')
    }
  }

  const handleRepeat = (blank: SavedOrderBlankDto) => {
    for (const line of blank.lines) {
      if (line.catalog_item_id == null || blank.private_supplier_id == null) {
        continue
      }
      addToCart({
        catalogItemId: line.catalog_item_id,
        name: line.name,
        privateSupplierId: blank.private_supplier_id,
        privateSupplierName: blank.supplier_name,
        quantity: line.quantity,
        unit: formatUnitLabel(line.unit),
        unitPrice: line.unit_price,
        note: line.note,
        brand: line.brand,
      })
    }
    setSubTab('cart')
  }

  const handleView = async (blank: SavedOrderBlankDto) => {
    try {
      const text = appendOrderBlankPromo(
        await fetchSavedOrderBlankText(organizationId, blank.id),
      )
      setViewText(text)
    } catch (error) {
      setHistoryError(error instanceof Error ? error.message : 'Не удалось открыть бланк')
    }
  }

  return (
    <section className="rp-screen" aria-label="Корзина">
      <div className="rp-subtabs" role="tablist" aria-label="Корзина и история">
        <button
          type="button"
          role="tab"
          aria-selected={subTab === 'cart'}
          className={subTab === 'cart' ? 'rp-subtab rp-subtab--active' : 'rp-subtab'}
          onClick={() => setSubTab('cart')}
        >
          Корзина
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={subTab === 'history'}
          className={subTab === 'history' ? 'rp-subtab rp-subtab--active' : 'rp-subtab'}
          onClick={() => setSubTab('history')}
        >
          История заявок
        </button>
      </div>

      {subTab === 'cart' ? (
        groups.length === 0 ? (
          <div className="echat-inbox__empty">
            <p className="echat-inbox__empty-title">Корзина пуста</p>
            <p className="echat-muted">Добавьте позиции из поиска по прайсам.</p>
          </div>
        ) : (
          <div className="rp-stack">
            {groups.map((group) => (
              <article key={group.supplierId} className="rp-group">
                <header className="rp-group__head">
                  <h3 className="rp-group__title">{shortenSupplierName(group.supplierName)}</h3>
                  <p className="rp-group__total">{formatMoney(group.total)}</p>
                </header>
                <ul className="rp-list">
                  {group.lines.map((line) => (
                    <li key={line.catalogItemId} className="rp-card rp-card--compact">
                      <div className="rp-card__main">
                        <p className="rp-card__title">{line.name}</p>
                        <p className="rp-card__meta">
                          {line.unitPrice != null
                            ? `${formatMoney(line.unitPrice)} / ${formatUnitLabel(line.unit)}`
                            : formatUnitLabel(line.unit)}
                        </p>
                      </div>
                      <div className="rp-stepper" role="group" aria-label="Количество">
                        <button
                          type="button"
                          className="rp-stepper__btn"
                          aria-label="Уменьшить"
                          onClick={() => setCartQuantity(line.catalogItemId, line.quantity - 1)}
                        >
                          −
                        </button>
                        <span className="rp-stepper__qty">{line.quantity}</span>
                        <button
                          type="button"
                          className="rp-stepper__btn"
                          aria-label="Увеличить"
                          onClick={() => setCartQuantity(line.catalogItemId, line.quantity + 1)}
                        >
                          +
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  className="echat-btn echat-btn--primary"
                  onClick={() => void openSheet(group.supplierId)}
                >
                  Сформировать заказ →
                </button>
              </article>
            ))}
          </div>
        )
      ) : (
        <div className="rp-stack">
          {historyLoading ? <p className="echat-muted">Загрузка…</p> : null}
          {historyError ? (
            <p className="echat-error" role="alert">
              {historyError}
            </p>
          ) : null}
          {!historyLoading && history.length === 0 ? (
            <div className="echat-inbox__empty">
              <p className="echat-inbox__empty-title">История пуста</p>
              <p className="echat-muted">Сохранённые бланки появятся после шаринга заказа.</p>
            </div>
          ) : null}
          <ul className="rp-list">
            {history.map((blank) => (
              <li key={blank.id} className="rp-card">
                <div className="rp-card__main">
                  <p className="rp-card__title">{blank.supplier_name}</p>
                  <p className="rp-card__meta">
                    {formatDate(blank.created_at)} · {blank.line_count} поз.
                    {blank.total_amount != null ? ` · ${formatMoney(blank.total_amount)}` : ''}
                  </p>
                </div>
                <div className="echat-actions">
                  <button
                    type="button"
                    className="echat-btn echat-btn--primary"
                    onClick={() => handleRepeat(blank)}
                  >
                    Повторить
                  </button>
                  <button
                    type="button"
                    className="echat-btn echat-btn--ghost"
                    onClick={() => void handleView(blank)}
                  >
                    Посмотреть
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {sheetGroup ? (
        <div className="rp-sheet" role="dialog" aria-modal="true" aria-labelledby="blank-title">
          <button
            type="button"
            className="rp-sheet__scrim"
            aria-label="Закрыть бланк заказа"
            onClick={() => setSheetSupplierId(null)}
          />
          <div className="rp-sheet__panel">
            <div className="rp-sheet__grip" aria-hidden="true" />
            <div className="rp-sheet__head">
              <h3 id="blank-title" className="rp-sheet__title">
                Бланк заказа
              </h3>
              <p className="rp-sheet__sub">{shortenSupplierName(sheetGroup.supplierName)}</p>
            </div>
            <div className="rp-sheet__body">
              <label className="echat-field">
                <span className="echat-field__label">Желаемая дата доставки</span>
                <span className="rp-datefield">
                  <span className="rp-datefield__value">
                    {deliveryDate ? formatRuDate(deliveryDate) : 'Выберите дату'}
                  </span>
                  <input
                    type="date"
                    className="echat-field__input rp-datefield__native"
                    value={deliveryDate}
                    min={new Date().toISOString().slice(0, 10)}
                    onChange={(event) => setDeliveryDate(event.target.value)}
                  />
                </span>
              </label>
              <label className="echat-field">
                <span className="echat-field__label">Комментарий поставщику</span>
                <textarea
                  className="echat-field__input"
                  rows={2}
                  value={comment}
                  onChange={(event) => setComment(event.target.value)}
                  placeholder="Например: доставка до 10:00, звонок за час"
                />
              </label>
              {blankLoading ? <p className="echat-muted">Формируем текст…</p> : null}
              {blankText ? <pre className="rp-blank">{blankText}</pre> : null}
              {actionError ? (
                <p className="echat-error" role="alert">
                  {actionError}
                </p>
              ) : null}
            </div>
            <div className="rp-sheet__foot rp-sheet__foot--split">
              <button
                type="button"
                className="echat-btn echat-btn--ghost"
                disabled={!blankText}
                onClick={() => void handleCopy()}
              >
                {copied ? 'Скопировано ✓' : 'Скопировать'}
              </button>
              <button
                type="button"
                className="echat-btn echat-btn--primary"
                disabled={!blankText}
                onClick={() => void handleShare()}
              >
                Поделиться
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {viewText ? (
        <div className="rp-sheet" role="dialog" aria-modal="true" aria-labelledby="view-title">
          <button
            type="button"
            className="rp-sheet__scrim"
            aria-label="Закрыть просмотр бланка"
            onClick={() => setViewText(null)}
          />
          <div className="rp-sheet__panel">
            <div className="rp-sheet__grip" aria-hidden="true" />
            <div className="rp-sheet__head">
              <h3 id="view-title" className="rp-sheet__title">
                Бланк заказа
              </h3>
            </div>
            <div className="rp-sheet__body">
              <pre className="rp-blank">{viewText}</pre>
            </div>
            <div className="rp-sheet__foot">
              <button type="button" className="echat-btn echat-btn--ghost" onClick={() => setViewText(null)}>
                Закрыть
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  )
}
